import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// ============================================================================
// Beszerzés (új): beszállítók és termékeik, rendelési lista (Emese), beszerzési
// rendelések jóváhagyással. Backend: src/backend/src/purchasing (api/purchasing/*).
// ============================================================================

export type SupplierOrderMethod = 'email' | 'webshop';

export interface SupplierItem {
  id: number;
  supplier_id: number;
  name: string;
  supplier_sku: string | null;
  url: string | null;
  unit_price_huf: string | number | null;
  price_checked_at: string | null;
  pack_size: number | null;
  usual_qty: number | null;
  component_id: number | null;
  sort_order: number;
  active: number;
  note: string | null;
}

export interface Supplier {
  id: number;
  name: string;
  email: string | null;
  website: string | null;
  order_method: SupplierOrderMethod;
  payment_method: string;
  /** a termékárak bruttók (vevői webshop) */
  prices_gross: number | boolean;
  contact_name: string | null;
  language: string;
  min_order_huf: number | null;
  shipping_fee_huf: number | null;
  lead_time_days: number | null;
  note: string | null;
  archived_at: string | null;
  items?: SupplierItem[];
}

export interface PurchaseSettings {
  checkDays: string;
  remindTimes: string;
  responsibleUserId: number | null;
  escalateUserId: number | null;
  autoOrderLimitHuf: number;
  deliveryNote: string;
  people: { id: number; name: string }[];
}

export interface PurchaseListLine {
  itemId: number;
  name: string;
  supplierSku: string | null;
  url: string | null;
  unitPrice: number | null;
  packSize: number | null;
  usualQty: number | null;
  decision: 'ok' | 'order' | null;
  quantity: number | null;
}

export interface PurchaseListView {
  listId: number;
  lastSubmittedAt: string | null;
  autoOrderLimitHuf: number;
  responsible: string | null;
  checkDays: string;
  suppliers: { id: number; name: string; orderMethod: SupplierOrderMethod; pricesGross?: boolean; shippingFee: number | null; minOrder: number | null; lines: PurchaseListLine[] }[];
}

export type PurchaseOrderStatus = 'pending' | 'approved' | 'sent' | 'confirmed' | 'shipped' | 'received' | 'cancelled';

export interface PurchaseOrderView {
  id: number;
  displayId: string;
  status: PurchaseOrderStatus;
  statusLabel: string;
  needsApproval: boolean;
  supplier: { id: number; name: string; orderMethod: SupplierOrderMethod; email: string | null; website: string | null; paymentMethod: string; pricesGross?: boolean } | null;
  robotConfirmed?: boolean;
  /** a webshop-robot utolsó futása */
  robotRun?: { id: number; mode: 'dry' | 'live'; status: 'running' | 'ready' | 'ordered' | 'failed' | 'blocked'; totalHuf: number | null; orderRef: string | null; note: string | null; hasScreenshot: boolean; createdAt: string; finishedAt: string | null } | null;
  netTotal: number | null;
  grossTotal: number | null;
  items: { id: number; name: string; supplierSku: string | null; url: string | null; quantity: number; unitPrice: number | null; receivedQty: number; componentId: number | null }[];
  emailSubject: string | null;
  emailBody: string | null;
  mailThreadId: number | null;
  supplierOrderRef: string | null;
  trackingCode: string | null;
  note: string | null;
  autoSent: boolean;
  createdAt: string;
  approvedAt: string | null;
  sentAt: string | null;
  confirmedAt: string | null;
  shippedAt: string | null;
  receivedAt: string | null;
  defaultEmail?: { subject: string; body: string };
}

export interface OrderProfile {
  company_name: string | null;
  tax_number: string | null;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  country: string;
  zip: string | null;
  city: string | null;
  street: string | null;
  note: string | null;
  ship_same: boolean;
  ship_name: string | null;
  ship_phone: string | null;
  ship_country: string | null;
  ship_zip: string | null;
  ship_city: string | null;
  ship_street: string | null;
  robot_dry_run: boolean;
  robot_enabled: boolean;
  missing: string[];
}

export interface WebshopPrice {
  url: string;
  name: string | null;
  sku: string | null;
  price: number | null;
  currency: string | null;
  inStock: boolean | null;
  availability: string | null;
}

/** Bruttó becslés: nettó × 1,27 + szállítás (ugyanaz a képlet, mint a backendben). */
export const VAT_RATE = 0.27;

/** Kiszereléshez kerekítés (felfelé). */
export function roundToPack(quantity: number, pack?: number | null): number {
  const p = Number(pack) || 1;
  return p > 1 ? Math.ceil(quantity / p) * p : quantity;
}

@Injectable({ providedIn: 'root' })
export class PurchasingService {
  constructor(private http: HttpClient) {}

  getSettings(): Observable<PurchaseSettings> {
    return this.http.get<PurchaseSettings>('purchasing/settings');
  }

  updateSettings(dto: Partial<{ check_days: string; remind_times: string; responsible_user_id: number | null; escalate_user_id: number | null; auto_order_limit_huf: number; delivery_note: string }>): Observable<PurchaseSettings> {
    return this.http.patch<PurchaseSettings>('purchasing/settings', dto);
  }

  getProfile(): Observable<OrderProfile> {
    return this.http.get<OrderProfile>('purchasing/profile');
  }

  updateProfile(dto: Partial<OrderProfile>): Observable<OrderProfile> {
    return this.http.patch<OrderProfile>('purchasing/profile', dto);
  }

  deleteSupplier(id: number): Observable<{ deleted: boolean; archived: boolean }> {
    return this.http.delete<{ deleted: boolean; archived: boolean }>(`purchasing/suppliers/${id}`);
  }

  clearList(): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>('purchasing/list');
  }

  deleteOrder(id: number): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`purchasing/orders/${id}`);
  }

  robot(id: number): Observable<PurchaseOrderView> {
    return this.http.post<PurchaseOrderView>(`purchasing/orders/${id}/robot`, {});
  }

  robotGo(id: number): Observable<PurchaseOrderView> {
    return this.http.post<PurchaseOrderView>(`purchasing/orders/${id}/robot-go`, {});
  }

  robotScreenshot(runId: number): Observable<Blob> {
    return this.http.get(`purchasing/robot-runs/${runId}/screenshot`, { responseType: 'blob' });
  }

  listSuppliers(archived = false): Observable<Supplier[]> {
    return this.http.get<Supplier[]>('purchasing/suppliers', { params: archived ? { archived: '1' } : {} });
  }

  saveSupplier(id: number | null, dto: Partial<Supplier>): Observable<Supplier> {
    return id ? this.http.patch<Supplier>(`purchasing/suppliers/${id}`, dto) : this.http.post<Supplier>('purchasing/suppliers', dto);
  }

  archiveSupplier(id: number, archived: boolean): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`purchasing/suppliers/${id}/archive`, { archived });
  }

  saveItem(supplierId: number, id: number | null, dto: Partial<Omit<SupplierItem, 'active'>> & { active?: boolean }): Observable<SupplierItem> {
    return id
      ? this.http.patch<SupplierItem>(`purchasing/suppliers/${supplierId}/items/${id}`, dto)
      : this.http.post<SupplierItem>(`purchasing/suppliers/${supplierId}/items`, dto);
  }

  deleteItem(supplierId: number, id: number): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`purchasing/suppliers/${supplierId}/items/${id}`);
  }

  checkPrice(itemId: number): Observable<{ item: SupplierItem; web: WebshopPrice }> {
    return this.http.post<{ item: SupplierItem; web: WebshopPrice }>(`purchasing/items/${itemId}/check-price`, {});
  }

  getList(): Observable<PurchaseListView> {
    return this.http.get<PurchaseListView>('purchasing/list');
  }

  setLine(itemId: number, decision: 'ok' | 'order' | null, quantity: number | null): Observable<{ ok: boolean }> {
    return this.http.patch<{ ok: boolean }>(`purchasing/list/lines/${itemId}`, { decision, quantity });
  }

  submitList(): Observable<{ orders: PurchaseOrderView[] }> {
    return this.http.post<{ orders: PurchaseOrderView[] }>('purchasing/list/submit', {});
  }

  listOrders(filter: { status?: string; active?: boolean } = {}): Observable<PurchaseOrderView[]> {
    const params: Record<string, string> = {};
    if (filter.status) params['status'] = filter.status;
    if (filter.active) params['active'] = '1';
    return this.http.get<PurchaseOrderView[]>('purchasing/orders', { params });
  }

  getOrder(id: number): Observable<PurchaseOrderView> {
    return this.http.get<PurchaseOrderView>(`purchasing/orders/${id}`);
  }

  approve(id: number): Observable<PurchaseOrderView> {
    return this.http.post<PurchaseOrderView>(`purchasing/orders/${id}/approve`, {});
  }

  send(id: number, subject: string, body: string): Observable<PurchaseOrderView> {
    return this.http.post<PurchaseOrderView>(`purchasing/orders/${id}/send`, { subject, body });
  }

  markOrdered(id: number, ref: string): Observable<PurchaseOrderView> {
    return this.http.post<PurchaseOrderView>(`purchasing/orders/${id}/ordered`, { ref });
  }

  update(id: number, dto: { status?: 'confirmed' | 'shipped'; supplier_order_ref?: string; tracking_code?: string; note?: string }): Observable<PurchaseOrderView> {
    return this.http.patch<PurchaseOrderView>(`purchasing/orders/${id}`, dto);
  }

  receive(id: number): Observable<PurchaseOrderView> {
    return this.http.post<PurchaseOrderView>(`purchasing/orders/${id}/receive`, {});
  }

  cancel(id: number): Observable<PurchaseOrderView> {
    return this.http.post<PurchaseOrderView>(`purchasing/orders/${id}/cancel`, {});
  }
}
